/**
 * MockupTemplateDetailModal — détail + édition d'un template existant.
 *
 * Sections :
 *   - Image source (présigned URL via /api/media/signed)
 *   - Métadonnées Vision (room_type, frame_style, palette, lum/sat) avec
 *     bouton "Relancer Vision" (sync, ~5s) si non extraites ou pour
 *     re-générer après une correction.
 *   - Tags chips avec source visuel (Vision = ✨ discret, Manual = plein)
 *   - Édition rapide (name, kind, active, notes) via PATCH
 *
 * Pour rééditer la mask_zone, ouvrir un sous-éditeur (futur — S4 MVP =
 * lecture seule sur la zone, l'utilisatrice supprime + recrée si besoin).
 */

import { useEffect, useState } from 'react'
import { Crop, Sparkles, Trash2 } from 'lucide-react'
import { toast } from 'sonner'

import Badge from '@/components/ui/Badge'
import Button from '@/components/ui/Button'
import Field from '@/components/ui/Field'
import Input from '@/components/ui/Input'
import Modal from '@/components/ui/Modal'
import Select from '@/components/ui/Select'
import Textarea from '@/components/ui/Textarea'
import { useConfirm } from '@/contexts/confirm-dialog-context'
import {
  useDeleteMockupTemplate,
  useMockupTemplate,
  useRelaunchTemplateVision,
  useUpdateMockupTemplate,
} from '@/hooks/useMockupTemplates'
import {
  FRAME_STYLE_FR,
  MOCKUP_TEMPLATE_KIND_FR,
  MOCKUP_TEMPLATE_KIND_OPTIONS,
  ROOM_TYPE_FR,
  type MaskZone,
  type MockupTemplateKind,
} from '@/types/mockup-templates'

import MaskZoneEditor from './MaskZoneEditor'

import './MockupTemplateDetailModal.css'

interface MockupTemplateDetailModalProps {
  templateId: number
  onClose: () => void
}

export default function MockupTemplateDetailModal({
  templateId,
  onClose,
}: MockupTemplateDetailModalProps) {
  const { confirm } = useConfirm()
  const detailQuery = useMockupTemplate(templateId)
  const updateMutation = useUpdateMockupTemplate()
  const deleteMutation = useDeleteMockupTemplate()
  const relaunchMutation = useRelaunchTemplateVision()

  const [name, setName] = useState('')
  const [kind, setKind] = useState<MockupTemplateKind>('lifestyle_room')
  const [active, setActive] = useState(true)
  const [notes, setNotes] = useState('')

  // Édition de la mask_zone — par défaut la zone (auto-extraite pour PSD,
  // dessinée à la création pour image plate) est affichée en lecture seule.
  // L'utilisatrice clique "Ajuster manuellement" pour la modifier.
  const [editingMask, setEditingMask] = useState(false)
  const [maskDraft, setMaskDraft] = useState<MaskZone | null>(null)

  const tpl = detailQuery.data
  const imageUrl = tpl?.image_url ?? null

  // Sync local form state on first load + sur changement d'id du template.
  // On ne re-sync pas sur chaque refetch silencieux (qui changerait `tpl`
  // sans changement sémantique) — l'utilisatrice ne veut pas voir ses
  // modifications en cours écrasées par un refresh background.
  /* eslint-disable react-hooks/set-state-in-effect, react-hooks/exhaustive-deps */
  useEffect(() => {
    if (!tpl) return
    setName(tpl.name)
    setKind(tpl.kind)
    setActive(tpl.active ?? true)
    setNotes(tpl.notes ?? '')
    setEditingMask(false)
    setMaskDraft(null)
  }, [tpl?.id])
  /* eslint-enable react-hooks/set-state-in-effect, react-hooks/exhaustive-deps */

  const handleSave = () => {
    if (!tpl) return
    updateMutation.mutate(
      {
        id: tpl.id,
        payload: {
          name: name.trim() !== tpl.name ? name.trim() : undefined,
          kind: kind !== tpl.kind ? kind : undefined,
          active: active !== tpl.active ? active : undefined,
          notes: notes !== (tpl.notes ?? '') ? notes : undefined,
        },
      },
      {
        onSuccess: () => toast.success('Template mis à jour'),
        onError: (err) => toast.error(err.detail ?? 'Mise à jour impossible'),
      },
    )
  }

  const handleStartEditMask = () => {
    if (!tpl) return
    setMaskDraft(tpl.mask_zone as MaskZone)
    setEditingMask(true)
  }

  const handleCancelEditMask = () => {
    setEditingMask(false)
    setMaskDraft(null)
  }

  const handleSaveMask = () => {
    if (!tpl || !maskDraft) return
    updateMutation.mutate(
      { id: tpl.id, payload: { mask_zone: maskDraft } },
      {
        onSuccess: () => {
          toast.success('Zone de placement mise à jour')
          setEditingMask(false)
          setMaskDraft(null)
        },
        onError: (err) => toast.error(err.detail ?? 'Sauvegarde impossible'),
      },
    )
  }

  const handleRelaunchVision = () => {
    if (!tpl) return
    relaunchMutation.mutate(
      { id: tpl.id, sync: true },
      {
        onSuccess: () => toast.success('Vision Tier 1 relancée'),
        onError: (err) => toast.error(err.detail ?? 'Vision indisponible'),
      },
    )
  }

  const handleDelete = async () => {
    if (!tpl) return
    const ok = await confirm({
      title: 'Supprimer ce template ?',
      message: `« ${tpl.name} » sera retiré de la bibliothèque. L'image source sera supprimée de MinIO.`,
      variant: 'danger',
      confirmText: 'Supprimer',
    })
    if (!ok) return
    deleteMutation.mutate(tpl.id, {
      onSuccess: () => {
        toast.success(`Template « ${tpl.name} » supprimé`)
        onClose()
      },
      onError: (err) => toast.error(err.detail ?? 'Suppression impossible'),
    })
  }

  if (!tpl && !detailQuery.isLoading) return null

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={tpl?.name ?? 'Chargement…'}
      size="form"
      kicker="Bibliothèque · Template"
    >
      <Modal.Body className="mockup-template-detail-body">
        {detailQuery.isLoading || !tpl ? (
          <p className="ui-body ui-text-muted">Chargement…</p>
        ) : (
          <>
            {imageUrl ? (
              <div className="mockup-template-detail-image">
                <img src={imageUrl} alt={tpl.name} />
                <span className="mockup-template-detail-image-meta">
                  {tpl.width} × {tpl.height} px · {(tpl.bytes / 1024 / 1024).toFixed(1)} Mo
                  {tpl.source_format === 'psd_smart_object' && <Badge variant="default">PSD</Badge>}
                </span>
              </div>
            ) : null}

            {imageUrl && (
              <section className="mockup-template-detail-section">
                <div className="mockup-template-detail-section-header">
                  <h3 className="ui-h5">Zone de placement</h3>
                  {!editingMask && (
                    <Button variant="ghost" size="sm" onClick={handleStartEditMask}>
                      <Crop size={14} aria-hidden="true" />
                      Ajuster manuellement
                    </Button>
                  )}
                </div>

                <MaskZoneEditor
                  imageUrl={imageUrl}
                  imageWidth={tpl.width}
                  imageHeight={tpl.height}
                  value={editingMask && maskDraft ? maskDraft : (tpl.mask_zone as MaskZone)}
                  onChange={setMaskDraft}
                  readOnly={!editingMask}
                />

                {editingMask && (
                  <div className="mockup-template-detail-mask-actions">
                    <Button variant="ghost" size="sm" onClick={handleCancelEditMask}>
                      Annuler
                    </Button>
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={handleSaveMask}
                      loading={updateMutation.isPending}
                    >
                      Enregistrer la zone
                    </Button>
                  </div>
                )}
                {!editingMask && tpl.source_format === 'psd_smart_object' && (
                  <p className="mockup-template-detail-mask-hint">
                    Zone extraite automatiquement du Smart Object PSD. Clique « Ajuster manuellement
                    » si tu veux la modifier.
                  </p>
                )}
              </section>
            )}

            <section className="mockup-template-detail-section">
              <div className="mockup-template-detail-section-header">
                <h3 className="ui-h5">Vision Tier 1</h3>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleRelaunchVision}
                  loading={relaunchMutation.isPending}
                >
                  <Sparkles size={14} aria-hidden="true" />
                  {tpl.vision_extracted_at ? 'Re-extraire' : 'Lancer Vision'}
                </Button>
              </div>

              {tpl.vision_extracted_at ? (
                <div className="mockup-template-detail-vision-grid">
                  <Field label="Pièce">
                    {() => (
                      <span className="mockup-template-detail-vision-value">
                        {tpl.room_type ? ROOM_TYPE_FR[tpl.room_type] : '—'}
                        {tpl.room_type_overridden ? <Badge variant="default">Manuel</Badge> : null}
                      </span>
                    )}
                  </Field>
                  <Field label="Cadre">
                    {() => (
                      <span className="mockup-template-detail-vision-value">
                        {tpl.frame_style ? FRAME_STYLE_FR[tpl.frame_style] : '—'}
                        {tpl.frame_style_overridden ? (
                          <Badge variant="default">Manuel</Badge>
                        ) : null}
                      </span>
                    )}
                  </Field>
                  {tpl.wall_color_hex ? (
                    <Field label="Mur">
                      {() => (
                        <span className="mockup-template-detail-vision-value">
                          <span
                            className="mockup-template-detail-color-swatch"
                            style={{ background: tpl.wall_color_hex ?? undefined }}
                            aria-hidden="true"
                          />
                          {tpl.wall_color_hex}
                        </span>
                      )}
                    </Field>
                  ) : null}
                  {tpl.palette_dominant && tpl.palette_dominant.length > 0 ? (
                    <Field label="Palette">
                      {() => (
                        <div className="mockup-template-detail-palette">
                          {tpl.palette_dominant?.map((entry) => (
                            <span
                              key={entry.hex}
                              className="mockup-template-detail-color-swatch"
                              style={{ background: entry.hex }}
                              title={`${entry.name_en} ${entry.hex}`}
                              aria-label={entry.name_en}
                            />
                          ))}
                        </div>
                      )}
                    </Field>
                  ) : null}
                </div>
              ) : (
                <p className="ui-body ui-text-muted">
                  Pas encore extraite. Clique « Lancer Vision » pour générer les descripteurs et les
                  tags.
                </p>
              )}
            </section>

            <section className="mockup-template-detail-section">
              <h3 className="ui-h5">Tags ({tpl.tags?.length ?? 0})</h3>
              {tpl.tags && tpl.tags.length > 0 ? (
                <div className="mockup-template-detail-tags">
                  {tpl.tags.map((tag) => (
                    <span
                      key={tag.tag_id}
                      className="mockup-template-detail-tag"
                      data-source={tag.source}
                    >
                      {tag.source === 'vision' ? <Sparkles size={11} aria-hidden="true" /> : null}
                      {tag.label_fr}
                      <span className="mockup-template-detail-tag-kind">{tag.kind}</span>
                    </span>
                  ))}
                </div>
              ) : (
                <p className="ui-body ui-text-muted">
                  Aucun tag pour le moment. Lance Vision Tier 1 pour générer des tags automatiques.
                </p>
              )}
            </section>

            <section className="mockup-template-detail-section">
              <h3 className="ui-h5">Édition</h3>
              <div className="mockup-template-detail-edit-grid">
                <Field label="Nom">
                  {(props) => (
                    <Input {...props} value={name} onChange={(e) => setName(e.target.value)} />
                  )}
                </Field>
                <Field label="Type">
                  {(props) => (
                    <Select
                      {...props}
                      value={kind}
                      onChange={(e) => setKind(e.target.value as MockupTemplateKind)}
                    >
                      {MOCKUP_TEMPLATE_KIND_OPTIONS.map((k) => (
                        <option key={k} value={k}>
                          {MOCKUP_TEMPLATE_KIND_FR[k]}
                        </option>
                      ))}
                    </Select>
                  )}
                </Field>
                <Field label="État">
                  {(props) => (
                    <Select
                      {...props}
                      value={active ? 'on' : 'off'}
                      onChange={(e) => setActive(e.target.value === 'on')}
                    >
                      <option value="on">Actif</option>
                      <option value="off">Désactivé</option>
                    </Select>
                  )}
                </Field>
                <Field label="Notes atelier">
                  {(props) => (
                    <Textarea
                      {...props}
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      rows={2}
                    />
                  )}
                </Field>
              </div>
            </section>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="quiet"
          type="button"
          onClick={() => void handleDelete()}
          loading={deleteMutation.isPending}
        >
          <Trash2 size={14} aria-hidden="true" />
          Supprimer
        </Button>
        <Button variant="ghost" type="button" onClick={onClose}>
          Fermer
        </Button>
        <Button
          variant="primary"
          type="button"
          onClick={handleSave}
          loading={updateMutation.isPending}
          disabled={!tpl}
        >
          Enregistrer
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
